import { useState } from 'react';
import { Check, AlertTriangle, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { UiDestination } from '@/hooks/useDestinations';
import {
  buildRecommendationExplanation,
  type RecommendationPrefs,
  type Reason,
} from '@/lib/recommendation-reasons';
import type { MonthlyIntelligenceRow } from '@/lib/destination-monthly-intelligence';

interface Props {
  destination: UiDestination;
  preferences: RecommendationPrefs;
  /** This destination's 12 monthly-intelligence rows, if available. Omit for
   * destinations outside the V1 research set -- the explanation still works,
   * it just won't include the month-specific chips. */
  monthlyRows?: MonthlyIntelligenceRow[];
  className?: string;
}

/**
 * Compact, mobile-first summary of why a destination fits the trip.
 *
 * Shows the top 3 positives and top 1 trade-off as at-a-glance chips.
 * Extra detail is available behind a collapsed "See why this matched" toggle.
 */
export const RecommendationExplanation = ({ destination, preferences, monthlyRows = [], className }: Props) => {
  const [open, setOpen] = useState(false);
  const { positives, tradeoffs } = buildRecommendationExplanation(destination, preferences, monthlyRows);

  const primaryPositives = positives.slice(0, 3);
  const primaryTradeoff = tradeoffs.slice(0, 1);
  const extraPositives = positives.slice(3);
  const extraTradeoffs = tradeoffs.slice(1);
  const hasMore = extraPositives.length + extraTradeoffs.length > 0;

  return (
    <div className={cn('text-left', className)}>
      <ul className="flex flex-wrap gap-1.5">
        {primaryPositives.map((r) => (
          <PositiveChip key={r.key} reason={r} />
        ))}
        {primaryTradeoff.map((r) => (
          <TradeoffChip key={r.key} reason={r} />
        ))}
      </ul>

      {hasMore && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpen((v) => !v);
            }}
            aria-expanded={open}
            className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            {open ? 'Hide details' : 'More details'}
            <ChevronDown
              className={cn('h-3 w-3 transition-transform', open && 'rotate-180')}
              aria-hidden
            />
          </button>


          {open && (
            <div className="mt-2 space-y-2">
              {extraPositives.length > 0 && (
                <ul className="space-y-1">
                  {extraPositives.map((r) => (
                    <DetailRow key={r.key} reason={r} kind="positive" />
                  ))}
                </ul>
              )}
              {extraTradeoffs.length > 0 && (
                <ul className="space-y-1">
                  {extraTradeoffs.map((r) => (
                    <DetailRow key={r.key} reason={r} kind="tradeoff" />
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const PositiveChip = ({ reason }: { reason: Reason }) => (
  <li
    title={reason.text}
    className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-[1px] text-[10.5px] font-medium text-emerald-800 leading-none whitespace-nowrap"
  >
    <Check className="h-2.5 w-2.5 shrink-0" aria-hidden />
    <span>{reason.label}</span>
  </li>
);

const TradeoffChip = ({ reason }: { reason: Reason }) => (
  <li
    title={reason.text}
    className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-1.5 py-[1px] text-[10.5px] font-medium text-amber-800 leading-none whitespace-nowrap"
  >
    <AlertTriangle className="h-2.5 w-2.5 shrink-0" aria-hidden />
    <span>{reason.label}</span>
  </li>
);


const DetailRow = ({ reason, kind }: { reason: Reason; kind: 'positive' | 'tradeoff' }) => (
  <li className="flex items-start gap-2 text-xs text-muted-foreground">
    {kind === 'positive' ? (
      <Check className="h-3 w-3 mt-0.5 shrink-0 text-emerald-500" aria-hidden />
    ) : (
      <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0 text-amber-500" aria-hidden />
    )}
    <span className="leading-snug">{reason.text}</span>
  </li>
);
